import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import ConnectionPanel from './components/ConnectionPanel';
import BasicUserPanel from './components/BasicUserPanel';
import CaptchaPanel from './components/CaptchaPanel';
import PropertyDataPanel from './components/PropertyDataPanel';
import LogPanel from './components/LogPanel';
import PaymentModal from './components/PaymentModal';
import PaymentCallback from './components/PaymentCallback';
import useWebSocket from './hooks/useWebSocket';
import useSearchCounter from './hooks/useSearchCounter';
import appLogo from './assets/lastnik.png';

function App() {
  const appMode = (process.env.REACT_APP_APP_MODE || 'admin').toLowerCase();
  const isUserMode = appMode === 'user';
  const defaultWsUrl = process.env.REACT_APP_WS_URL || 'ws://localhost:5110/ws';
  const [wsUrl, setWsUrl] = useState(defaultWsUrl);
  const [address, setAddress] = useState('');
  const [logs, setLogs] = useState([]);
  const [captchaData, setCaptchaData] = useState(null);
  const [propertyData, setPropertyData] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [propertyDataBlurred, setPropertyDataBlurred] = useState(false);
  const [userError, setUserError] = useState(null);
  
  // Search counter hook
  const searchCounter = useSearchCounter();
  const pendingQuotaUpdate = useRef(null);

  function normalizeCaptchaImage(rawValue) {
    if (!rawValue) return null;

    const normalized = rawValue.trim().replace(/\s+/g, '');
    if (!normalized) return null;

    if (normalized.startsWith('data:image/')) {
      return normalized;
    }

    return `data:image/png;base64,${normalized}`;
  }

  function extractCaptchaImage(message) {
    if (!message || typeof message !== 'string') {
      return null;
    }

    const marker = '#serverStart#';
    const markerIndex = message.indexOf(marker);

    if (markerIndex >= 0) {
      const start = markerIndex + marker.length;
      const endMarkerIndex = message.indexOf('#end#', start);
      const payload = endMarkerIndex >= 0 ? message.substring(start, endMarkerIndex) : message.substring(start);
      return normalizeCaptchaImage(payload);
    }

    const dataImageIndex = message.indexOf('data:image/');
    if (dataImageIndex >= 0) {
      return normalizeCaptchaImage(message.substring(dataImageIndex));
    }

    return null;
  }

  function parseServerJsonMessage(message) {
    if (!message || typeof message !== 'string') {
      return null;
    }

    try {
      const parsed = JSON.parse(message);
      if (!parsed || typeof parsed !== 'object') {
        return null;
      }

      return parsed;
    } catch {
      return null;
    }
  }

  function formatPropertyData(payload) {
    if (!payload || typeof payload !== 'object') {
      return 'Podatki o nepremicnini so prejeti.';
    }

    const owners = Array.isArray(payload.owners)
      ? payload.owners
      : Array.isArray(payload.Owners)
        ? payload.Owners
        : [];

    const parcelNumber = payload.parcelNumber || payload.ParcelNumber || 'neznano';
    const municipality = payload.katastrskaObcina || payload.KatastrskaObcina || 'neznano';

    return `Prejeti podatki: KO ${municipality}, parcela ${parcelNumber}, lastnikov ${owners.length}.`;
  }
  
  const { isConnected, connect, disconnect, sendMessage } = useWebSocket(wsUrl, {
    onMessage: handleMessage,
    onConnect: (info) => addLog(`Povezava uspesna: ${info?.url || wsUrl}`, 'success'),
    onDisconnect: (event) => {
      const closeCode = event?.code ?? 'neznano';
      const closeReason = event?.reason || 'brez razloga';
      const closeType = event?.wasClean ? 'cista prekinitev' : 'necista prekinitev';

      if (Number(closeCode) === 402 || Number(closeCode) === 1008) {
        addLog('Iskanje ni mogoce: dosezena je kvota iskanj. Prosim, aktivirajte placljiv racun.', 'warning');
        searchCounter.refreshQuota();
        setShowPaymentModal(true);
        setCaptchaData(null);
        return;
      }

      // 1006 = abnormal closure (e.g. HTTP 402 rejected before WebSocket upgrade)
      if (Number(closeCode) === 1006) {
        searchCounter.refreshQuota().then((result) => {
          if (result?.requiresPayment) {
            addLog('Iskanje ni mogoce: dosezena je kvota iskanj. Prosim, aktivirajte placljiv racun.', 'warning');
            setShowPaymentModal(true);
          } else {
            addLog(`Povezava s streznikom je prekinjena (koda ${closeCode})`, 'warning');
          }
        });
        setCaptchaData(null);
        return;
      }

      const isExpectedCompletion = event?.wasClean && Number(closeCode) === 1000;
      if (isExpectedCompletion) {
        addLog(`Streznik je zakljucil obdelavo (${closeType}, koda ${closeCode}, razlog: ${closeReason})`, 'success');
      } else {
        addLog(`Povezava s streznikom je prekinjena (${closeType}, koda ${closeCode}, razlog: ${closeReason})`, 'warning');
      }

      setCaptchaData(null);
      // propertyData is intentionally kept so the popup stays open after server closes connection
    },
    onError: (error) => addLog(`Napaka: ${error}`, 'error')
  });

  function addLog(message, type = 'info') {
    setLogs(prev => [...prev, {
      id: Date.now() + Math.random(),
      timestamp: new Date().toLocaleTimeString(),
      message,
      type
    }]);
  }

  function handleMessage(data) {
    const preview = String(data).slice(0, 120);
    addLog(`Sporocilo s streznika: ${preview}${String(data).length > 120 ? '...' : ''}`, 'info');

    const parsedMessage = parseServerJsonMessage(data);
    if (parsedMessage) {
      const action = String(parsedMessage.action || parsedMessage.Action || '').toUpperCase();
      const payload = parsedMessage.data || parsedMessage.Data;

      if (action === 'CAPTCHA_REQUIRED') {
        const captchaImage = normalizeCaptchaImage(payload?.captchaImageBase64 || payload?.CaptchaImageBase64);
        if (captchaImage) {
          setCaptchaData(captchaImage);
          addLog('Streznik zahteva CAPTCHA - prosim resite jo.', 'warning');
          return;
        }

        addLog('Streznik je zahteval CAPTCHA, vendar manjka slika.', 'error');
        return;
      }

      if (action === 'QUOTA_UPDATE') {
        // Always buffer; apply counter + payment modal after results are shown
        pendingQuotaUpdate.current = payload || {};
        return;
      }

      if (action === 'PROPERTY_DATA') {
        setPropertyData(payload);
        addLog(formatPropertyData(payload), 'success');
        // Apply buffered quota update now that results are displayed
        if (pendingQuotaUpdate.current) {
          searchCounter.updateFromWebSocket(pendingQuotaUpdate.current);
          if (pendingQuotaUpdate.current.requiresPayment) {
            setPropertyDataBlurred(true);
          }
          pendingQuotaUpdate.current = null;
        }
        return;
      }

      if (action === 'ERROR') {
        const errDetail = parsedMessage.error || parsedMessage.Error
          || payload?.message || payload?.Message
          || 'neznana napaka';
        if (String(errDetail).startsWith('ADDRESS_NOT_FOUND')) {
          addLog(`Naslov ni bil najden. Preverite naslov in poskusite znova.`, 'error');
          setUserError('Naslov ni bil najden.');
        } else {
          addLog(`Napaka streznika: ${errDetail}`, 'error');
          setUserError(`Napaka: ${errDetail}`);
        }
        return;
      }

      addLog(`Prejeta akcija streznika: ${action || 'NEZNANO'}`, 'info');
      return;
    }

    const captchaImage = extractCaptchaImage(data);
    if (captchaImage) {
      setCaptchaData(captchaImage);
      addLog('CAPTCHA je prejeta - prosim resite jo.', 'warning');
    } else {
      addLog(`Streznik: ${data}`, 'success');
    }
  }

  function handleConnect() {
    if (!wsUrl.trim()) {
      addLog('Manjka WebSocket URL. Nastavite REACT_APP_WS_URL v datoteki .env.', 'error');
      return;
    }

    if (!address.trim()) {
      addLog('Pred povezavo vnesite naslov nepremicnine.', 'error');
      return;
    }
    addLog(`Povezujem na ${wsUrl} za naslov: ${address}...`, 'info');
    const startMessage = JSON.stringify({
      action: 'START_SEARCH',
      data: { address: address.trim() }
    });
    connect(startMessage);
  }

  function handleUserSearch() {
    if (!wsUrl.trim()) {
      addLog('Manjka WebSocket URL. Nastavite REACT_APP_WS_URL v datoteki .env.', 'error');
      return;
    }

    if (!address.trim()) {
      addLog('Pred iskanjem vnesite naslov nepremicnine.', 'error');
      return;
    }

    const startMessage = JSON.stringify({
      action: 'START_SEARCH',
      data: { address: address.trim() }
    });

    addLog(`Iscem lastniske podatke za: ${address}...`, 'info');
    setUserError(null);
    setPropertyData(null);
    setPropertyDataBlurred(false);
    pendingQuotaUpdate.current = null;
    connect(startMessage);
  }

  function handleDisconnect() {
    addLog('Prekinjam povezavo...', 'info');
    disconnect();
  }

  function handleCaptchaSolve(solution) {
    if (solution) {
      const wasSent = sendMessage(solution);
      if (wasSent) {
        addLog(`CAPTCHA resitev poslana: ${solution}`, 'success');
        setCaptchaData(null);
      } else {
        addLog('CAPTCHA resitve ni mogoce poslati: povezava ni aktivna', 'error');
      }
    }
  }

  function handlePropertyDataClose() {
    setPropertyData(null);
    setPropertyDataBlurred(false);
    searchCounter.refreshQuota();
  }

  useEffect(() => {
    if (isUserMode) {
      addLog('Uporabniski nacin je pripravljen. Vnesite naslov in kliknite Isci.', 'info');
    } else {
      addLog('Admin nacin je pripravljen. Vnesite URL streznika in kliknite Povezi.', 'info');
    }
  }, [isUserMode]);

  const callbackRoutes = ['/payment-success', '/payment-error', '/payment-cancel', '/payment-callback'];
  if (callbackRoutes.includes(window.location.pathname.replace(/\/$/, ''))) {
    return <PaymentCallback />;
  }

  return (
    <div className={`App ${isUserMode ? 'user-mode' : 'admin-mode'}`}>
      <div className={`container ${isUserMode ? 'user-container' : ''}`}>
        {!isUserMode && (
          <header className="app-header">
            <img src={appLogo} alt="Kdo je lastnik logo" className="app-logo" />
            <h1>Kdo je lastnik? Admin</h1>
            <p className="subtitle">Nadzorna plošča</p>
          </header>
        )}

        {isUserMode ? (
          <BasicUserPanel
            address={address}
            setAddress={setAddress}
            isConnected={isConnected}
            onSearch={handleUserSearch}
            searchCounter={searchCounter}
            onPay={() => setShowPaymentModal(true)}
            userError={userError}
          />
        ) : (
          <ConnectionPanel
            wsUrl={wsUrl}
            setWsUrl={setWsUrl}
            address={address}
            setAddress={setAddress}
            isConnected={isConnected}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
          />
        )}

        <CaptchaPanel
          captchaData={captchaData}
          onSolve={handleCaptchaSolve}
          onCancel={() => setCaptchaData(null)}
        />

        <PropertyDataPanel
          propertyData={propertyData}
          onClose={handlePropertyDataClose}
          blurred={propertyDataBlurred}
          onUnlock={() => { setShowPaymentModal(true); }}
        />

        {!isUserMode && <LogPanel logs={logs} />}

        {isUserMode && (
          <PaymentModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
          />
        )}

      </div>
    </div>
  );
}

export default App;
